<script setup lang="ts">
  import { object } from 'yup'
  import { useAuthStore } from '~/stores/useAuthStore'
  import { useValidationSchemas } from '~/composables/useValidationSchemas'

  const emit = defineEmits<{
    (e: 'loading' | 'changed' | 'editing', value: boolean): void
  }>()

  defineProps<{
    isEditing: boolean
  }>()

  const { t } = useI18n()
  const store = useAuthStore()
  const toast = useToast()
  const form = ref()

  const { currentPasswordSchema, passwordFieldsSchema } = useValidationSchemas()

  const schema = computed(() => {
    return object({
      currentPassword: currentPasswordSchema,
      passwords: passwordFieldsSchema,
    })
  })

  const initialState = {
    currentPassword: '',
    passwords: {
      newPassword: '',
      confirmNewPassword: '',
    },
  }

  const state = reactive(JSON.parse(JSON.stringify(initialState)))

  watch(
    () => [
      state.currentPassword,
      state.passwords.newPassword,
      state.passwords.confirmNewPassword,
    ],
    () => {
      const dirty =
        state.currentPassword !== initialState.currentPassword ||
        state.passwords.newPassword !== initialState.passwords.newPassword ||
        state.passwords.confirmNewPassword !==
          initialState.passwords.confirmNewPassword
      emit('changed', dirty)
    },
    { deep: true }
  )

  onMounted(() => {
    emit('editing', true)
  })

  const resetForm = () => {
    Object.assign(state, JSON.parse(JSON.stringify(initialState)))
    form.value?.reset()
  }

  const onSave = async () => {
    try {
      const isValid = await form.value.validate()
      if (!isValid) return

      emit('loading', true)

      const success = await store.changePassword({
        currentPassword: state.currentPassword,
        newPassword: state.passwords.newPassword,
        confirmNewPassword: state.passwords.confirmNewPassword,
      })

      toast.add({
        description: success
          ? t('pages.me.change-password.toast.success')
          : t('pages.me.change-password.toast.error'),
        color: success ? 'success' : 'error',
      })

      if (success) {
        emit('changed', true)
        resetForm()
      }
    } finally {
      emit('loading', false)
    }
  }

  defineExpose({
    onSave,
    reset: resetForm,
  })
</script>

<template>
  <div class="flex w-80 flex-col items-center justify-center gap-4 pt-5">
    <span>{{ t('pages.me.change-password.title') }}</span>

    <UForm
      ref="form"
      :schema="schema"
      :state="state"
      class="flex w-full flex-col gap-2"
    >
      <UFormField
        name="currentPassword"
        :label="$t('pages.me.change-password.current-password-title')"
      >
        <InputPassword
          v-model="state.currentPassword"
          :disabled="!isEditing"
          :placeholder="$t('pages.me.change-password.current-password')"
        />
      </UFormField>

      <PasswordFields v-model="state.passwords" />
    </UForm>
  </div>
</template>
